import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTeamChat } from '@/context/TeamChatContext';
import { Message } from '@team-connect/types/team-chat';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import { MessageSquare, Pencil, Trash2, Check, X, Download, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import EmojiPicker from './EmojiPicker';
import ReactMarkdown from 'react-markdown';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import VoiceMessagePlayer from './VoiceMessagePlayer';
import LinkPreview from './LinkPreview';

// Helper to safely convert plain URLs to markdown links while ignoring existing markdown links
const linkifyText = (text: string) => {
  const regex = /\[.*?\]\(.*?\)|\bhttps?:\/\/[^\s<]+[^<.,:;"')\]\s]/g;
  return text.replace(regex, (match) => {
    if (match.startsWith('[')) return match;
    return `[${match}](${match})`;
  });
};

// Helper to extract unique URLs for previews
const extractUrls = (text: string): string[] => {
  const urls: string[] = [];
  const regex = /https?:\/\/[^\s<]+[^<.,:;"')\]\s]/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    urls.push(match[0]);
  }
  return [...new Set(urls)];
};

interface MessageListProps {
  messages: Message[];
  isGlobalDarkMode: boolean;
  hasRightPanel?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, isGlobalDarkMode, hasRightPanel = false }) => {
  const { getUserById, setActiveThread, threads, toggleReaction, currentUser, messages: allMessages, editMessage, deleteMessage, hasMoreMessages, isLoadingMore, loadMoreMessages, typingUsers, activeChannelId, activeDmId, onlineUsers, isChannelMember } = useTeamChat();
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  // Check if user is a member (only for channels, not DMs)
  const isMember = activeChannelId ? isChannelMember(activeChannelId) : true;

  const [previewData, setPreviewData] = useState<{
    attachments: { name: string; size: number; type: string; url: string }[];
    currentIndex: number;
  } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevHeightRef = useRef<number>(0);

  // Zoom & Pan state for Lightbox
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const channelKey = messages.length > 0 ? (messages[0]?.channelId || messages[0]?.dmId) : null;
  useEffect(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [channelKey]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!hasMoreMessages || isLoadingMore) return;
    
    // Trigger load when scrolled near the top
    if (el.scrollTop < 100) {
      prevHeightRef.current = el.scrollHeight;
      loadMoreMessages();
    }
  }, [hasMoreMessages, isLoadingMore, loadMoreMessages]);

  useEffect(() => {
    // When messages change length (pagination load finished), restore scroll anchor
    const el = scrollRef.current;
    if (el && prevHeightRef.current > 0) {
      const newHeight = el.scrollHeight;
      el.scrollTop = newHeight - prevHeightRef.current;
      prevHeightRef.current = 0;
    }
  }, [messages.length]);

  useEffect(() => {
    // Attempt scroll to bottom if new message arrived (instead of pagination)
    if (prevHeightRef.current === 0) {
      const el = scrollRef.current;
      if (el) {
        // Only auto scroll down if the user is already somewhat near the bottom
        const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
        if (isNearBottom) {
          el.scrollTop = el.scrollHeight;
        }
      }
    }
  }, [messages.length]);

  const openPreview = (attachments: { name: string; size: number; type: string; url: string }[], index: number) => {
    resetZoom();
    setPreviewData({ attachments, currentIndex: index });
  };

  const closePreview = () => {
    resetZoom();
    setPreviewData(null);
  };

  const handleNext = useCallback((e?: React.MouseEvent | KeyboardEvent) => {
    e?.stopPropagation();
    resetZoom();
    setPreviewData(prev => prev ? ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.attachments.length
    }) : null);
  }, [resetZoom]);

  const handlePrevious = useCallback((e?: React.MouseEvent | KeyboardEvent) => {
    e?.stopPropagation();
    resetZoom();
    setPreviewData(prev => prev ? ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.attachments.length) % prev.attachments.length
    }) : null);
  }, [resetZoom]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!previewData) return;
      if (e.key === 'Escape') closePreview();
      if (e.key === 'ArrowLeft') handlePrevious(e);
      if (e.key === 'ArrowRight') handleNext(e);
      // Reset zoom on R
      if (e.key === 'r' || e.key === 'R') resetZoom();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewData, handlePrevious, handleNext, resetZoom]);

  const handleZoomWheel = (e: React.WheelEvent) => {
    if (currentFile?.type.startsWith('image/')) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.min(Math.max(1, prev + delta), 4));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const formatMessageTime = (date: Date) => {
    return format(date, 'h:mm a');
  };

  const formatDateDivider = (date: Date) => {
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'EEEE, MMMM d');
  };

  const getThreadInfo = (messageId: string) => {
    return threads.find(t => t.parentMessageId === messageId);
  };

  const startEditing = (message: Message) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const saveEdit = () => {
    if (editingMessageId && editContent.trim()) {
      editMessage(editingMessageId, editContent.trim());
      setEditingMessageId(null);
      setEditContent('');
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    }
    if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const groupedMessages: { date: string; messages: Message[] }[] = [];
  let currentDate = '';

  messages.forEach(message => {
    const messageDate = formatDateDivider(new Date(message.timestamp));
    if (messageDate !== currentDate) {
      currentDate = messageDate;
      groupedMessages.push({ date: messageDate, messages: [message] });
    } else {
      groupedMessages[groupedMessages.length - 1].messages.push(message);
    }
  });

  const highlightMentions = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (typeof child !== 'string') return child;
      const parts = child.split(/(@\w+)/g);
      if (parts.length === 1) return child;
      return parts.map((part, i) =>
        /^@\w+/.test(part) ? (
          <span key={i} className={cn(
            "font-semibold rounded px-0.5",
            isGlobalDarkMode ? "text-workspace-accent bg-workspace-accent/20" : "text-primary bg-primary/10"
          )}>{part}</span>
        ) : part
      );
    });
  };

  const textColor = isGlobalDarkMode ? 'text-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500';
  const dividerColor = isGlobalDarkMode ? 'bg-border' : 'bg-gray-200';

  const currentFile = previewData ? previewData.attachments[previewData.currentIndex] : null;
  
  const activeTypers = typingUsers.filter(t => t.channelId === (activeChannelId || activeDmId));

  return (
    <div ref={scrollRef} onScroll={handleScroll} className={cn("flex-1 overflow-y-auto mobile-hide-scrollbar pl-6 md:pl-10 py-4 flex flex-col gap-4", hasRightPanel ? "pr-4" : "pr-6 md:pr-10")}>
      {hasMoreMessages && (
        <div className="text-center py-2 shrink-0">
          <button
            onClick={() => loadMoreMessages()}
            className={cn("text-xs transition-colors shrink-0", subTextColor, isGlobalDarkMode ? "hover:text-gray-200" : "hover:text-foreground")}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : 'Load earlier messages'}
          </button>
        </div>
      )}
      {groupedMessages.map((group, groupIndex) => (
        <div key={groupIndex} className="space-y-4">
          {/* Date Divider */}
          <div className="flex items-center my-6">
            <div className={cn("flex-1 h-px", dividerColor)} />
            <span className={cn("px-4 text-[11px] font-bold uppercase tracking-wider", subTextColor)}>
              {group.date}
            </span>
            <div className={cn("flex-1 h-px", dividerColor)} />
          </div>

          {/* Messages */}
          {group.messages.map((message) => {
            const user = getUserById(message.userId);
            const threadInfo = getThreadInfo(message.id);
            const parentMessage = message.threadParentId
              ? allMessages.find(m => m.id === message.threadParentId)
              : null;
            const parentUser = parentMessage ? getUserById(parentMessage.userId) : null;
            const isOwn = currentUser ? message.userId === currentUser.id : false;
            const isEditing = editingMessageId === message.id;
            
            const messageUrls = extractUrls(message.content).filter(url => !message.hiddenUrlPreviews?.includes(url));

            return (
              <div
                key={message.id}
                className={cn(
                  "group relative flex gap-3 px-3 py-2 rounded-lg transition-colors duration-200",
                  isGlobalDarkMode ? "hover:bg-card/50" : "hover:bg-gray-100/50"
                )}
              >
                <div className="relative shrink-0 self-start">
                  <Avatar className="w-9 h-9 mt-0.5">
                    <AvatarImage src={user?.avatar} alt={user?.displayName} />
                    <AvatarFallback className={cn("text-sm", isGlobalDarkMode ? "bg-card text-muted-foreground" : "bg-primary text-primary-foreground")}>
                      {user?.displayName.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  {/* Online indicator - only show for other users */}
                  {user && !isOwn && (
                    <span className={cn(
                      "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 transition-colors",
                      isGlobalDarkMode ? "border-background" : "border-white",
                      onlineUsers.includes(user.id) ? 'bg-green-500' : 'bg-gray-400'
                    )} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className={cn("font-bold text-sm", textColor)}>
                      {isOwn ? 'You' : (user?.displayName || 'Unknown')}
                    </span>
                    <span className={cn("text-[10px] sm:text-xs", subTextColor)}>
                      {formatMessageTime(new Date(message.timestamp))}
                    </span>
                    {message.isEdited && (
                      <span className={cn("text-[10px] italic", subTextColor)}>(edited)</span>
                    )}
                  </div>

                  {/* Thread reference indicator */}
                  {parentMessage && (
                    <button
                      onClick={() => setActiveThread(parentMessage.id)}
                      className={cn("flex items-center gap-1.5 text-xs mb-1 hover:opacity-80 transition-opacity", subTextColor)}
                    >
                      <MessageSquare className="w-3 h-3" />
                      <span>
                        Replied to <span className={cn("font-medium", textColor)}>{parentUser?.displayName || 'Unknown'}</span>
                      </span>
                      <span className={cn("truncate max-w-[200px] opacity-70 italic")}>
                        "{parentMessage.content.slice(0, 50)}{parentMessage.content.length > 50 ? '...' : ''}"
                      </span>
                    </button>
                  )}

                  {isEditing ? (
                    <div className="mt-1 max-w-[600px]">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        className={cn(
                          "min-h-[44px] max-h-[200px] resize-none text-sm border-2 focus-visible:ring-0",
                          isGlobalDarkMode ? "bg-background border-border text-foreground" : "bg-white border-primary/20"
                        )}
                        autoFocus
                        rows={1}
                      />
                      <div className="flex items-center gap-1 mt-1">
                        <Button size="sm" variant="ghost" className={cn("h-7 text-xs", isGlobalDarkMode && "hover:bg-card text-muted-foreground")} onClick={cancelEditing}>
                          <X className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" className="h-7 text-xs bg-workspace-accent hover:bg-workspace-accent-hover text-white" onClick={saveEdit} disabled={!editContent.trim()}>
                          <Check className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <span className={cn("text-[10px] ml-2", subTextColor)}>Esc to cancel · Enter to save</span>
                      </div>
                    </div>
                  ) : (
                    <div className={cn(
                      "text-sm mt-0.5 whitespace-pre-wrap break-words max-w-full sm:max-w-[80%]",
                      textColor,
                      "[&>p]:mt-0 [&>p]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4",
                      isGlobalDarkMode ? "[&>code]:bg-gray-800 [&>code]:text-gray-200 [&>pre]:bg-gray-800/50" : "[&>code]:bg-muted [&>pre]:bg-muted"
                    )}>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p>{highlightMentions(children)}</p>,
                          a: ({ node, ...props }) => (
                            <a 
                              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline" 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              {...props} 
                            />
                          )
                        }}
                      >{linkifyText(message.content)}</ReactMarkdown>
                    </div>
                  )}

                  {/* URL Previews */}
                  {messageUrls.length > 0 && !isEditing && (
                    <div className="flex flex-col gap-2 mt-2">
                      {messageUrls.map((url, idx) => (
                        <LinkPreview key={`${url}-${idx}`} url={url} isGlobalDarkMode={isGlobalDarkMode} />
                      ))}
                    </div>
                  )}

                  {/* Attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {message.attachments.map((file, i) => (
                        <div key={i} className="group/attachment relative transition-transform hover:scale-[1.01]">
                          {file.type.startsWith('audio/') ? (
                            <VoiceMessagePlayer
                              url={file.url}
                              duration={file.duration}
                              isGlobalDarkMode={isGlobalDarkMode}
                              isOwn={isOwn}
                            />
                          ) : file.type === 'image/gif' ? (
                            <div className="relative rounded-lg overflow-hidden max-w-[280px]">
                              <img
                                src={file.url}
                                alt={file.name || 'GIF'}
                                className="rounded-lg w-full h-auto object-cover max-h-[200px]"
                                loading="lazy"
                              />
                              <span className={cn(
                                "absolute bottom-1.5 left-1.5 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded",
                                "bg-black/50 text-white backdrop-blur-sm"
                              )}>
                                GIF
                              </span>
                            </div>
                          ) : file.type.startsWith('image/') ? (
                            <div
                              onClick={() => openPreview(message.attachments!, i)}
                              className="relative rounded-lg overflow-hidden border border-border max-w-[300px] max-h-[300px] cursor-zoom-in cursor-pointer"
                            >
                              <img src={file.url} alt={file.name} className="object-cover max-w-full max-h-[300px] w-auto h-auto" />
                            </div>
                          ) : (
                            <button
                              onClick={() => openPreview(message.attachments!, i)}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left",
                                isGlobalDarkMode ? "bg-card border-border hover:bg-accent" : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                              )}
                            >
                              <FileText className="w-4 h-4 opacity-70" />
                              <div className="flex flex-col">
                                <span className={cn("text-xs font-medium leading-none mb-0.5", textColor)}>{file.name}</span>
                                <span className={cn("text-[10px] opacity-60", textColor)}>{Math.round(file.size / 1024)}KB</span>
                              </div>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reactions */}
                  {message.reactions && message.reactions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 items-center">
                      {message.reactions.map((reaction) => {
                        const hasReacted = currentUser ? reaction.userIds.includes(currentUser.id) : false;
                        return (
                          <button
                            key={reaction.emoji}
                            onClick={() => isMember && toggleReaction(message.id, reaction.emoji)}
                            disabled={!isMember}
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all duration-200",
                              !isMember && "cursor-not-allowed opacity-70",
                              hasReacted
                                ? (isGlobalDarkMode ? "bg-workspace-accent/20 text-workspace-accent" : "bg-primary/10 text-primary")
                                : (isGlobalDarkMode ? "bg-card hover:bg-card/80 text-muted-foreground" : "bg-muted hover:bg-muted/80 text-foreground/70")
                            )}
                          >
                            <span>{reaction.emoji}</span>
                            <span className="font-semibold">{reaction.userIds.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Thread Reply Button */}
                  {threadInfo && (
                    <button
                      onClick={() => isMember && setActiveThread(message.id)}
                      disabled={!isMember}
                      className={cn(
                        "mt-2 flex items-center gap-2 text-xs font-semibold transition-all",
                        isMember ? "hover:underline" : "cursor-not-allowed opacity-70",
                        isGlobalDarkMode ? "text-workspace-accent" : "text-primary"
                      )}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>{threadInfo.replyCount} {threadInfo.replyCount === 1 ? 'reply' : 'replies'}</span>
                      <span className={cn("font-normal opacity-60")}>
                        Last reply {formatDistanceToNow(new Date(threadInfo.lastReplyAt), { addSuffix: true })}
                      </span>
                    </button>
                  )}
                </div>

                {/* Hover action toolbar - absolute top-right, no layout shift */}
                {!isEditing && isMember && (
                  <div className={cn(
                    "absolute top-1 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 [&:has([data-state=open])]:opacity-100 transition-opacity duration-150 z-10",
                    isGlobalDarkMode ? "bg-card/95 border border-border" : "bg-white/95 border border-gray-200",
                    "rounded-lg shadow-sm px-1 py-0.5"
                  )}>
                    <EmojiPicker
                      onSelect={(emoji) => toggleReaction(message.id, emoji)}
                      className="h-7 w-7"
                      variant="quick"
                      isGlobalDarkMode={isGlobalDarkMode}
                    />
                    <button
                      onClick={() => setActiveThread(message.id)}
                      title="Reply in thread"
                      className={cn(
                        "h-7 w-7 rounded flex items-center justify-center transition-colors",
                        subTextColor,
                        isGlobalDarkMode ? "hover:bg-accent hover:text-foreground" : "hover:bg-gray-100 hover:text-foreground"
                      )}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                    {isOwn && (
                      <>
                        <button
                          onClick={() => startEditing(message)}
                          title="Edit message"
                          className={cn(
                            "h-7 w-7 rounded flex items-center justify-center transition-colors",
                            subTextColor,
                            isGlobalDarkMode ? "hover:bg-accent hover:text-foreground" : "hover:bg-gray-100 hover:text-foreground"
                          )}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteMessage(message.id)}
                          title="Delete message"
                          className={cn(
                            "h-7 w-7 rounded flex items-center justify-center transition-colors",
                            isGlobalDarkMode ? "text-muted-foreground hover:bg-accent hover:text-red-400" : "text-gray-400 hover:bg-gray-100 hover:text-destructive"
                          )}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Typing Indicator */}
      {activeTypers.length > 0 && (
        <div className="flex px-3 py-1">
          <span className={cn("text-xs font-medium italic animate-pulse", subTextColor)}>
            {activeTypers.length === 1
               ? `${getUserById(activeTypers[0].userId)?.displayName || 'Someone'} is typing...`
               : `${activeTypers.length} people are typing...`
            }
          </span>
        </div>
      )}

      {/* Lightbox / Preview Modal */}
      {currentFile && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex flex-col bg-black/95 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={closePreview}
        >
          {/* Toolbar */}
          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-black/40 text-white border-b border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col min-w-0 flex-1">
              <h3 className="font-medium text-sm text-white/90 truncate">{currentFile.name}</h3>
              <span className="text-xs text-white/50">{currentFile.type} · {previewData && `${previewData.currentIndex + 1} of ${previewData.attachments.length}`}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <a
                href={currentFile.url}
                download={currentFile.name}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </a>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
                title="Close"
              >
                <X className="w-5 sm:w-6 h-5 sm:h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            className="flex-1 flex items-center justify-center p-4 overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Navigation Buttons */}
            {previewData && previewData.attachments.length > 1 && (
              <>
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-[10000]"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-[10000]"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {currentFile.type.startsWith('image/') ? (
              <div 
                className="w-full h-full flex items-center justify-center overflow-hidden"
                onWheel={handleZoomWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  key={currentFile.url}
                  src={currentFile.url}
                  alt={currentFile.name}
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                  }}
                  className="max-w-full max-h-full object-contain shadow-2xl rounded-sm animate-in fade-in duration-200 select-none pointer-events-none"
                  draggable={false}
                />
                
                {/* Zoom Level Indicator */}
                {zoom > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resetZoom();
                    }}
                    className="absolute bottom-6 right-6 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-[10px] font-bold tracking-widest uppercase rounded-full backdrop-blur-md border border-white/10 transition-all hover:scale-105 active:scale-95 animate-in fade-in zoom-in duration-200 z-[10001]"
                  >
                    {Math.round(zoom * 100)}% · Reset
                  </button>
                )}
              </div>
            ) : (
              <iframe
                key={currentFile.url}
                src={currentFile.url}
                className="w-full h-full max-w-5xl bg-white rounded-md shadow-2xl animate-in fade-in duration-200"
                title={currentFile.name}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MessageList;
